import axios from "axios";
// import { api } from "../config";

axios.defaults.baseURL = "";
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

/**
 * Decode JWT token to check expiration (without verification)
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
const decodeJWT = (token: string): any => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1];
    // Decode base64
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (e) {
    console.error('Error decoding JWT:', e);
    return null;
  }
};

/**
 * Check if JWT token is expired
 * @param token JWT token string
 * @returns true if expired, false if valid, null if can't determine
 */
const isTokenExpired = (token: string): boolean | null => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return null; // Can't determine
  }
  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  return currentTime >= expirationTime;
};

/**
 * Process queued requests after token refresh
 * @param error Error if refresh failed, null if successful
 * @param newToken New access token if refresh succeeded
 */
const processQueue = (error: any, newToken: string | null = null) => {
  isRefreshing = false;
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (newToken) {
      resolve(newToken);
    } else {
      reject(new Error('Token refresh failed'));
    }
  });
  failedQueue = [];
};

// content type
const authUser: any = sessionStorage.getItem("authUser")
let token = null;
if (authUser) {
  try {
    const userData = JSON.parse(authUser);
    token = userData.token || userData.access_token;
  } catch (e) {
    // Ignore parse errors
  }
}
if (token)
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;

// Request interceptor to ensure token is always included
axios.interceptors.request.use(
  function (config: any) {
    // Skip token checks for public endpoints (login, refresh, forgot-password)
    if (config.url && (
      config.url.includes('/auth/login') || 
      config.url.includes('/auth/refresh') ||
      config.url.includes('/auth/forgot-password')
    )) {
      console.log('🔐 Request interceptor - Public endpoint, skipping token check:', config.url);
      console.log('🔐 Request data:', config.data);
      // Remove Authorization header for public endpoints (user doesn't have token yet)
      if (config.headers) {
        delete config.headers.Authorization;
        delete config.headers.common?.Authorization;
      }
      console.log('🔐 Request headers (after removing Authorization):', config.headers);
      return config;
    }
    
    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ 
          resolve: (newToken: string) => {
            // Update config with new token and retry
            config.headers.Authorization = `Bearer ${newToken}`;
            resolve(axios(config));
          }, 
          reject 
        });
      });
    }
    
    // Get token from sessionStorage on every request (handles page refresh)
    const authUser = sessionStorage.getItem("authUser");
    if (authUser) {
      try {
        const userData = JSON.parse(authUser);
        const token = userData.token || userData.access_token;
        if (token) {
          // Only add token if it's not a demo token (demo tokens shouldn't be sent to backend)
          // Demo tokens start with "demo-token-"
          if (!token.startsWith('demo-token-')) {
            // Ensure token is trimmed and properly formatted
            const cleanToken = token.trim();
            
            // Check if token is expired before sending
            const expired = isTokenExpired(cleanToken);
            if (expired === true) {
              // Set refreshing flag immediately to prevent race conditions
              isRefreshing = true;
              console.warn('⏰ Token expired, refreshing...');
              
              // Attempt to refresh the token
              return refreshAccessToken().then((newTokenData) => {
                if (newTokenData && newTokenData.access_token) {
                  const newToken = newTokenData.access_token.trim();
                  
                  // Update config with new token
                  config.headers.Authorization = `Bearer ${newToken}`;
                  
                  // Process queued requests with new token
                  processQueue(null, newToken);
                  
                  console.log('✅ Token refreshed, retrying original request');
                  return axios(config);
                } else {
                  // Refresh failed, clear queue and redirect to login
                  processQueue(new Error('Token refresh failed'));
                  
                  console.warn('❌ Token refresh failed, redirecting to login');
                  if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                  }
                  return Promise.reject(new Error('Token expired and refresh failed'));
                }
              }).catch((error) => {
                processQueue(error);
                
                console.error('❌ Token refresh error:', error);
                if (window.location.pathname !== '/login') {
                  window.location.href = '/login';
                }
                return Promise.reject(error);
              });
            }
            
            config.headers.Authorization = `Bearer ${cleanToken}`;
            
            // Debug logging for /auth/me requests
            if (config.url && config.url.includes('/auth/me')) {
              console.log('🔐 Request interceptor - Adding token to /auth/me request');
              console.log('🔐 Token exists:', !!cleanToken);
              console.log('🔐 Token length:', cleanToken.length);
              console.log('🔐 Token starts with:', cleanToken.substring(0, 20) + '...');
              if (expired === false) {
                const decoded = decodeJWT(cleanToken);
                if (decoded && decoded.exp) {
                  const expirationDate = new Date(decoded.exp * 1000);
                  console.log('🔐 Token expires at:', expirationDate.toLocaleString());
                }
              }
            }
          } else {
            if (config.url && config.url.includes('/auth/me')) {
              console.log('🔐 Request interceptor - Skipping demo token for /auth/me');
            }
          }
        } else {
          if (config.url && config.url.includes('/auth/me')) {
            console.warn('⚠️ Request interceptor - No token found in sessionStorage for /auth/me');
          }
        }
      } catch (e) {
        console.error('❌ Request interceptor - Error parsing authUser from sessionStorage:', e);
      }
    } else {
      if (config.url && config.url.includes('/auth/me')) {
        console.warn('⚠️ Request interceptor - No authUser in sessionStorage for /auth/me');
      }
    }
    return config;
  },
  function (error: any) {
    return Promise.reject(error);
  }
);

// Response interceptor to capture errors
axios.interceptors.response.use(
  function (response:any) {
    // Log successful responses for login endpoint
    if (response?.config?.url?.includes('/auth/login')) {
      console.log('✅ Login response interceptor - Success:', response.status);
      console.log('✅ Login response data:', response.data);
    }
    return response.data ? response.data : response;
  },
  async function (error:any) {
    const originalRequest = error?.config;
    
    // Log all errors for login endpoint
    if (originalRequest?.url?.includes('/auth/login')) {
      console.error('❌ Login response interceptor - Error:', error);
      console.error('❌ Error response:', error?.response);
      console.error('❌ Error response status:', error?.response?.status);
      console.error('❌ Error response data:', error?.response?.data);
    }
    
    // Handle 401 Unauthorized errors - try to refresh token
    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const url = originalRequest?.url || '';
      
      // Skip refresh for login/refresh/change-password endpoints
      if (url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/change-password')) {
        console.log('🚫 Skipping token refresh for login/refresh/change-password endpoint');
        return Promise.reject(error);
      }
      
      // Skip refresh for demo tokens
      const authUser = sessionStorage.getItem("authUser");
      if (authUser) {
        try {
          const userData = JSON.parse(authUser);
          const token = userData.token || userData.access_token;
          if (token && token.startsWith('demo-token-')) {
            return Promise.reject(error);
          }
        } catch (e) {
          // Ignore
        }
      }
      
      // Mark this request to prevent infinite loop
      originalRequest._retry = true;
      
      console.warn('🔄 401 detected, refreshing token...');
      
      // Prevent multiple simultaneous refresh attempts
      if (isRefreshing) {
        // Queue this request to retry after refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ 
            resolve: (newToken: string) => {
              // Update the original request with new token and retry
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(axios(originalRequest));
            }, 
            reject 
          });
        });
      }
      
      isRefreshing = true;
      
      try {
        const newTokenData = await refreshAccessToken();
        
        if (newTokenData && newTokenData.access_token) {
          const newToken = newTokenData.access_token.trim();
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Process queued requests with new token
          processQueue(null, newToken);
          
          console.log('✅ Token refreshed, retrying original request');
          return axios(originalRequest);
        } else {
          // Refresh failed
          processQueue(new Error('Token refresh failed'));
          
          console.error('❌ Token refresh failed, redirecting to login');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError);
        
        console.error('❌ Token refresh error:', refreshError);
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    
    // For other errors or if refresh didn't apply, log and reject normally
    if (error?.response?.status === 401) {
      const url = error?.config?.url || '';
      console.error('❌ 401 Unauthorized error for:', url);
      
      // Log token information for debugging
      const authHeader = error?.config?.headers?.Authorization;
      if (authHeader) {
        console.error('🔐 Authorization header was present:', authHeader.substring(0, 20) + '...');
      } else {
        console.error('⚠️ No Authorization header in request');
      }
    }
    
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Don't transform the error here - let the calling code handle it
    // This preserves the full error object with response.data for detailed error handling
    return Promise.reject(error);
  }
);
/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: any) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

/**
 * Refresh the access token using refresh token
 * @returns Promise with new token data or null if refresh fails
 */
const refreshAccessToken = async (): Promise<any> => {
  const authUser = sessionStorage.getItem("authUser");
  if (!authUser) {
    return null;
  }

  try {
    const userData = JSON.parse(authUser);
    const refreshToken = userData.refresh_token;
    
    if (!refreshToken) {
      console.warn('⚠️ No refresh token available');
      return null;
    }

    // Call refresh endpoint (it's public, so no auth header needed)
    const API_BASE_URL = (window as any).process?.env?.REACT_APP_API_BASE_URL || 'http://localhost:8080';
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken: refreshToken
    });

    const tokenData = response.data?.data || response.data;
    const newAccessToken = tokenData?.access_token || tokenData?.token;
    const newRefreshToken = tokenData?.refresh_token || refreshToken; // Keep old refresh token if new one not provided

    if (newAccessToken) {
      // Update sessionStorage with new tokens
      const updatedUserData = {
        ...userData,
        token: newAccessToken,
        access_token: newAccessToken,
        refresh_token: newRefreshToken
      };
      sessionStorage.setItem("authUser", JSON.stringify(updatedUserData));
      
      // Update axios defaults
      setAuthorization(newAccessToken);
      
      console.log('✅ Token refreshed successfully');
      return { access_token: newAccessToken, refresh_token: newRefreshToken };
    }
    
    return null;
  } catch (error: any) {
    console.error('❌ Token refresh failed:', error);
    // Clear sessionStorage on refresh failure
    sessionStorage.removeItem("authUser");
    delete axios.defaults.headers.common["Authorization"];
    return null;
  }
};

class APIClient {
  /**
   * Fetches data from given url
   */

  //  get = (url, params) => {
  //   return axios.get(url, params);
  // };
  get = (url: any, params: any) => {
    // If params is null/undefined/empty object, just use the URL as-is
    // This handles cases where the URL already has query parameters built in
    if (!params || (typeof params === 'object' && Object.keys(params).length === 0)) {
      return axios.get(url);
    }

    // If URL already has query params, append with &
    // Otherwise, start with ?
    const hasQueryParams = url.includes('?');
    const separator = hasQueryParams ? '&' : '?';
    
    let paramKeys: any = [];
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        // Ensure value is a string and properly encoded
        const stringValue = String(value).trim();
        if (stringValue) {
          paramKeys.push(`${key}=${encodeURIComponent(stringValue)}`);
        }
      }
    });

    const queryString = paramKeys.length > 0 ? paramKeys.join('&') : '';
    const finalUrl = queryString ? `${url}${separator}${queryString}` : url;
    
    return axios.get(finalUrl);
  };
  /**
   * post given data to url
   */
  create = (url: any, data: any) => {
    return axios.post(url, data);
  };
  /**
   * Updates data
   */
  update = (url: any, data: any) => {
    return axios.patch(url, data);
  };

  put = (url: any, data: any) => {
    return axios.put(url, data);
  };
  /**
   * Delete
   */
  delete = (url: any, config: any) => {
    return axios.delete(url, { ...config });
  };
}
const getLoggedinUser = () => {

  const user = sessionStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

export { APIClient, setAuthorization, getLoggedinUser };